import { DAY, HOUR, MINUTE } from './types.js';
const categoryOf = (i) => i.category ?? i.kind;
/** Leverage = work unblocked × waiting time (hours, +1 so fresh items still rank by work). */
export function leverage(item, now) {
    const work = Math.max(1, item.unblocks) + (item.runWaiting ? 1 : 0);
    const wait = 1 + Math.max(0, now - item.createdAt) / HOUR;
    return Math.round(work * wait * 100) / 100;
}
const DIGEST_KINDS = new Set(['fyi', 'build']);
/**
 * Rank the Inbox: batch items per workstream into decision sessions of up to `maxPicks`
 * (one card with several picks), ordered by total leverage.
 */
export function rankInbox(items, now, opts = {}) {
    const maxPicks = opts.maxPicks ?? 5;
    const digest = [];
    const snoozed = [];
    const groups = new Map();
    for (const it of items) {
        if (DIGEST_KINDS.has(it.kind))
            digest.push(it);
        else if (it.snoozedUntil !== undefined && it.snoozedUntil > now)
            snoozed.push(it);
        else {
            const key = it.workstreamId ? `ws:${it.workstreamId}` : `project:${it.projectId}`;
            const g = groups.get(key) ?? [];
            g.push(it);
            groups.set(key, g);
        }
    }
    const sessions = [];
    for (const [key, g] of groups) {
        const ranked = g
            .map((item) => ({ item, leverage: leverage(item, now) }))
            .sort((a, b) => b.leverage - a.leverage ||
            a.item.createdAt - b.item.createdAt ||
            cmp(a.item.id, b.item.id));
        for (let i = 0; i < ranked.length; i += maxPicks) {
            const chunk = ranked.slice(i, i + maxPicks);
            const first = chunk[0]?.item;
            const s = {
                id: i === 0 ? key : `${key}#${i / maxPicks + 1}`,
                projectId: first.projectId,
                items: chunk,
                leverage: Math.round(chunk.reduce((a, x) => a + x.leverage, 0) * 100) / 100,
                oldestAt: Math.min(...chunk.map((x) => x.item.createdAt)),
            };
            if (first.workstreamId)
                s.workstreamId = first.workstreamId;
            sessions.push(s);
        }
    }
    sessions.sort((a, b) => b.leverage - a.leverage || a.oldestAt - b.oldestAt || cmp(a.id, b.id));
    return { sessions, digest, snoozed };
}
const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
// ---------------------------------------------------------------------------------------------
// Push
// ---------------------------------------------------------------------------------------------
/** Push only what blocks progress; everything else waits for the Inbox or the digest. */
export function isPushWorthy(item) {
    if (DIGEST_KINDS.has(item.kind))
        return { pushWorthy: false, why: `${item.kind} → digest` };
    if (item.kind === 'proposal' || item.kind === 'rule') {
        return { pushWorthy: false, why: `${item.kind}s batch in the Inbox` };
    }
    if (!item.blocking)
        return { pushWorthy: false, why: 'non-blocking (agent proceeds on an assumption)' };
    return { pushWorthy: true, why: `blocking ${item.kind}` };
}
export const DEFAULT_PUSH_CONFIG = {
    // G12: a blocked run is the most expensive thing to leave unseen; the old 4/h cap held asks for
    // up to an hour. Threads still merge, so a burst costs one notification.
    maxPerHour: 12,
    quietStart: '23:00',
    quietEnd: '08:00',
    timeZone: 'UTC',
    demoteBelowRate: 0.5,
    minSamplesForDemotion: 5,
    statsWindowMs: 14 * DAY,
    coalesceMs: 20_000,
};
export function pushStats(history, now, config = {}) {
    const c = { ...DEFAULT_PUSH_CONFIG, ...config };
    const out = {};
    for (const r of history) {
        if (now - r.at > c.statsWindowMs)
            continue;
        let s = out[r.category];
        if (!s) {
            s = { sent: 0, resolved: 0, actedOn: 0, rate: null, demoted: false };
            out[r.category] = s;
        }
        s.sent++;
        if (r.actedOn !== undefined) {
            s.resolved++;
            if (r.actedOn)
                s.actedOn++;
        }
    }
    for (const s of Object.values(out)) {
        s.rate = s.resolved ? s.actedOn / s.resolved : null;
        s.demoted = s.resolved >= c.minSamplesForDemotion && (s.rate ?? 1) < c.demoteBelowRate;
    }
    return out;
}
function parseHm(s) {
    const [h, m] = s.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
}
/** Minutes after local midnight in `timeZone`. */
export function localMinutes(now, timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date(now));
    const get = (t) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    return get('hour') * 60 + get('minute');
}
/** If `now` is in quiet hours, when they end; else null. Handles windows crossing midnight. */
export function quietHoursEnd(now, config = {}) {
    const c = { ...DEFAULT_PUSH_CONFIG, ...config };
    const cur = localMinutes(now, c.timeZone);
    const start = parseHm(c.quietStart);
    const end = parseHm(c.quietEnd);
    if (start === end)
        return null;
    const inQuiet = start < end ? cur >= start && cur < end : cur >= start || cur < end;
    if (!inQuiet)
        return null;
    const minsLeft = (end - cur + 24 * 60) % (24 * 60);
    const floorMin = now - (now % MINUTE);
    return floorMin + minsLeft * MINUTE;
}
/**
 * Decide which pushes go out now: push-worthy only, demoted categories suppressed, quiet hours,
 * one coalesced push per workstream thread, and a rolling hourly cap.
 */
export function planPushes(input) {
    const c = { ...DEFAULT_PUSH_CONFIG, ...input.config };
    const { now } = input;
    const held = [];
    const pushed = new Set(input.history.flatMap((r) => r.itemIds));
    const stats = pushStats(input.history, now, c);
    const quietEnd = quietHoursEnd(now, c);
    const threads = new Map();
    for (const it of input.candidates) {
        const worthy = isPushWorthy(it);
        if (!worthy.pushWorthy) {
            held.push({ itemId: it.id, reason: 'not_push_worthy', why: worthy.why });
            continue;
        }
        if (pushed.has(it.id)) {
            held.push({ itemId: it.id, reason: 'already_pushed', why: 'already pushed' });
            continue;
        }
        const cat = categoryOf(it);
        const st = stats[cat];
        if (st?.demoted && !it.urgent) {
            held.push({
                itemId: it.id,
                reason: 'demoted',
                why: `${cat} acted-on ${Math.round((st.rate ?? 0) * 100)}% < ${c.demoteBelowRate * 100}%; Inbox only`,
            });
            continue;
        }
        if (quietEnd !== null && !it.urgent) {
            held.push({ itemId: it.id, reason: 'quiet_hours', until: quietEnd, why: 'quiet hours' });
            continue;
        }
        const threadId = it.workstreamId ?? `project:${it.projectId}`;
        const t = threads.get(threadId) ?? { items: [], lev: 0 };
        t.items.push(it);
        t.lev = Math.max(t.lev, leverage(it, now));
        threads.set(threadId, t);
    }
    const recent = input.history.filter((r) => now - r.at < HOUR);
    let remaining = Math.max(0, c.maxPerHour - recent.length);
    const capFreesAt = Math.min(now, ...recent.map((r) => r.at)) + HOUR;
    const send = [];
    const ordered = [...threads].sort((a, b) => b[1].lev - a[1].lev || cmp(a[0], b[0]));
    for (const [threadId, t] of ordered) {
        const last = input.history
            .filter((r) => r.threadId === threadId)
            .reduce((m, r) => Math.max(m, r.at), Number.NEGATIVE_INFINITY);
        const urgent = t.items.some((i) => i.urgent);
        if (now - last < c.coalesceMs && !urgent) {
            for (const i of t.items) {
                held.push({
                    itemId: i.id,
                    reason: 'coalesced',
                    until: last + c.coalesceMs,
                    why: `thread pushed ${Math.round((now - last) / 1000)}s ago`,
                });
            }
            continue;
        }
        if (remaining <= 0 && !urgent) {
            for (const i of t.items) {
                held.push({
                    itemId: i.id,
                    reason: 'rate_cap',
                    until: capFreesAt,
                    why: `cap ${c.maxPerHour}/h reached`,
                });
            }
            continue;
        }
        if (!urgent)
            remaining--;
        const items = [...t.items].sort((a, b) => leverage(b, now) - leverage(a, now) || cmp(a.id, b.id));
        send.push({
            threadId,
            category: categoryOf(items[0]),
            itemIds: items.map((i) => i.id),
            count: items.length,
        });
    }
    return { send, held };
}
//# sourceMappingURL=inbox.js.map